import { assertNever } from "@redotech/util/type";
import type {
  AnalyticsBooleanExpression,
  AnalyticsExpression,
} from "./analytics-query-expression-types";
import {
  analyticsNameKey,
  isAnalyticsCatalogRef,
  isAnalyticsLocalRef,
} from "./analytics-query-refs";
import type {
  AnalyticsQuery,
  AnalyticsValueName,
} from "./analytics-query-types";

export type AnalyticsQueryPath = readonly (string | number)[];

/** One broken cross-reference inside a query: a definition, column or sort that names another wrongly. */
export interface AnalyticsQueryReferenceProblem {
  readonly path: AnalyticsQueryPath;
  readonly message: string;
}

/** Every cross-reference problem, in the order resolution reports them. */
export function analyticsQueryReferenceProblems(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  return [
    ...analyticsDuplicateDefinitions(query),
    ...analyticsUndefinedLocals(query),
    ...analyticsCircularDefinitions(query),
    ...analyticsDuplicateColumns(query),
    ...analyticsShownGrainKeys(query),
  ];
}

/** Each definition after the first with its name. */
export function analyticsDuplicateDefinitions(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  const seen = new Set<string>();
  const problems: AnalyticsQueryReferenceProblem[] = [];
  for (const [index, definition] of query.definitions.entries()) {
    if (seen.has(definition.name)) {
      problems.push({
        path: ["definitions", index, "name"],
        message: `duplicate definition ${definition.name}; give each definition its own name`,
      });
    }
    seen.add(definition.name);
  }
  return problems;
}

/** Each local name no definition declares, in definitions, columns, both filter lists, grain keys and sorts. A definition may use a later one. */
export function analyticsUndefinedLocals(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  const defined = new Set<string>(query.definitions.map(({ name }) => name));
  const problems: AnalyticsQueryReferenceProblem[] = [];
  const require = (name: string, path: AnalyticsQueryPath): void => {
    if (!defined.has(name)) {
      problems.push({
        path,
        message: `unknown name ${name}: no definition has it; define it, or name a catalog field`,
      });
    }
  };
  for (const [index, definition] of query.definitions.entries()) {
    visitLocalRefs(
      definition.expression,
      ["definitions", index, "expression"],
      require,
    );
  }
  const requireName = (
    name: AnalyticsValueName,
    path: AnalyticsQueryPath,
  ): void => {
    if (isAnalyticsLocalRef(name)) {
      require(name.name, path);
    }
  };
  for (const [index, column] of query.columns.entries()) {
    requireName(column.name, ["columns", index, "name"]);
  }
  for (const [index, name] of (query.filters ?? []).entries()) {
    requireName(name, ["filters", index]);
  }
  for (const [index, name] of (query.filtersAfterWindows ?? []).entries()) {
    requireName(name, ["filtersAfterWindows", index]);
  }
  for (const [index, name] of (query.grainKeys ?? []).entries()) {
    requireName(name, ["grainKeys", index]);
  }
  for (const [index, term] of (query.sorts ?? []).entries()) {
    requireName(term.name, ["sorts", index, "name"]);
  }
  return problems;
}

/** One problem per cycle among definitions, at the definition the cycle returns to. */
export function analyticsCircularDefinitions(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  const firstIndex = new Map<string, number>();
  for (const [index, definition] of query.definitions.entries()) {
    if (!firstIndex.has(definition.name)) {
      firstIndex.set(definition.name, index);
    }
  }
  const uses = new Map<string, Set<string>>();
  for (const definition of query.definitions) {
    const used = uses.get(definition.name) ?? new Set<string>();
    visitLocalRefs(definition.expression, [], (name) => {
      if (firstIndex.has(name)) {
        used.add(name);
      }
    });
    uses.set(definition.name, used);
  }
  const problems: AnalyticsQueryReferenceProblem[] = [];
  const finished = new Set<string>();
  const stack: { readonly name: string; readonly index: number }[] = [];
  const visit = (name: string, index: number): void => {
    if (finished.has(name)) {
      return;
    }
    const open = stack.findIndex((entry) => entry.name === name);
    if (open !== -1) {
      const chain = [...stack.slice(open).map((entry) => entry.name), name];
      problems.push({
        path: ["definitions", index, "name"],
        message: `circular definition ${name}: ${chain.join(" -> ")}; a definition cannot use itself, even through others`,
      });
      return;
    }
    stack.push({ name, index });
    for (const next of uses.get(name) ?? []) {
      visit(next, firstIndex.get(next) ?? index);
    }
    stack.pop();
    finished.add(name);
  };
  for (const [name, index] of firstIndex) {
    visit(name, index);
  }
  return problems;
}

/** Each column after the first with its name key: the key is the output alias. */
export function analyticsDuplicateColumns(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  const selected = new Set<string>();
  const problems: AnalyticsQueryReferenceProblem[] = [];
  for (const [index, column] of query.columns.entries()) {
    const key = analyticsNameKey(column.name);
    if (selected.has(key)) {
      problems.push({
        path: ["columns", index, "name"],
        message: `column ${key} is selected more than once; to show it twice, define a local with a different name`,
      });
    }
    selected.add(key);
  }
  return problems;
}

/** Each grain key that is a column or an earlier grain key. */
export function analyticsShownGrainKeys(
  query: AnalyticsQuery,
): readonly AnalyticsQueryReferenceProblem[] {
  const selected = new Set(
    query.columns.map((column) => analyticsNameKey(column.name)),
  );
  const keyed = new Set<string>();
  const problems: AnalyticsQueryReferenceProblem[] = [];
  for (const [index, name] of (query.grainKeys ?? []).entries()) {
    const key = analyticsNameKey(name);
    if (selected.has(key)) {
      problems.push({
        path: ["grainKeys", index],
        message: `grain key ${key} is also a column, which keys the result already; remove it from grainKeys`,
      });
    } else if (keyed.has(key)) {
      problems.push({
        path: ["grainKeys", index],
        message: `grain key ${key} is listed more than once; list it once`,
      });
    }
    keyed.add(key);
  }
  return problems;
}

/** Each sort on a definition that is neither a column nor, by `keysRows`, a key of the listed rows. */
export function analyticsUnselectedSorts(
  query: AnalyticsQuery,
  keysRows: (name: AnalyticsValueName) => boolean,
): readonly AnalyticsQueryReferenceProblem[] {
  const shown = new Set(
    query.columns.map((column) => analyticsNameKey(column.name)),
  );
  const problems: AnalyticsQueryReferenceProblem[] = [];
  for (const [index, term] of (query.sorts ?? []).entries()) {
    const name = analyticsNameKey(term.name);
    if (
      !isAnalyticsCatalogRef(term.name) &&
      !shown.has(name) &&
      !keysRows(term.name)
    ) {
      problems.push({
        path: ["sorts", index, "name"],
        message: `${name} is not a column; add ${name} to columns to sort by it`,
      });
    }
  }
  return problems;
}

function visitLocalRefs(
  node: AnalyticsExpression | AnalyticsBooleanExpression,
  path: AnalyticsQueryPath,
  visit: (name: string, path: AnalyticsQueryPath) => void,
): void {
  const child = (
    next: AnalyticsExpression | AnalyticsBooleanExpression,
    ...segments: readonly (string | number)[]
  ): void => visitLocalRefs(next, [...path, ...segments], visit);
  switch (node.kind) {
    case "ref":
      if (isAnalyticsLocalRef(node.name)) {
        visit(node.name.name, path);
      }
      return;
    case "constant":
      return;
    case "call":
      for (const [index, arg] of node.args.entries()) {
        child(arg, "args", index);
      }
      return;
    case "categorize":
      for (const [index, branch] of node.cases.entries()) {
        child(branch.when, "cases", index, "when");
        child(branch.value, "cases", index, "value");
      }
      child(node.otherwise, "otherwise");
      return;
    case "aggregate":
      child(node.of, "of");
      if (node.only !== undefined) {
        child(node.only, "only");
      }
      if ("per" in node && node.per?.keys !== undefined) {
        for (const [index, key] of node.per.keys.entries()) {
          child(key, "per", "keys", index);
        }
      }
      if ("by" in node && node.by !== undefined) {
        for (const [index, term] of node.by.entries()) {
          child(term.of, "by", index, "of");
        }
      }
      if ("sequence" in node && node.sequence !== undefined) {
        for (const [index, term] of node.sequence.entries()) {
          child(term.of, "sequence", index, "of");
        }
      }
      return;
    case "relative":
      for (const [index, term] of node.sequence.entries()) {
        child(term.of, "sequence", index, "of");
      }
      for (const [index, among] of (node.among ?? []).entries()) {
        child(among, "among", index);
      }
      if ("of" in node && typeof node.of !== "string") {
        child(node.of, "of");
      }
      if (node.only !== undefined) {
        child(node.only, "only");
      }
      return;
    default:
      return assertNever(node);
  }
}
